import App from "@bloglovin/inspire-engine/App";
import { Context } from "%/dynamic-component-editor/classes/context-v2";

const stripAppPrefix = type => String(type || "").split(".").at(-1);
const isEmptyRef = ref => ref === null || ref === undefined || ref === "" || ref === 0 || ref === "0";
const unwrap = maybe => (typeof maybe === "function" ? maybe() : maybe) ?? {};
const contextPkVars = ctx => ctx?.entityPkVars ?? ctx?.entityPkUrlVars;

// Cascade from base-story-card-element-mixin: injected per-row `pageEntities[name]`
// (already-resolved id) wins; else the scoped/global context bag via getEntityField.
// `sourceField` chains one hop: read that oneEntity relation off the source
// entity instead of its own pk. The per-row shortcut only holds for a direct
// bind — a hop still needs the source entity to read the relation from.
// Returns { id } on success, or { reason } naming what actually failed — the
// two cases (no context vs an empty relation) need different author fixes.
function resolveContextEntityId(name, scopedContexts, pageEntities, sourceField) {
  const fromPage = pageEntities[name];
  if (!sourceField && !isEmptyRef(fromPage)) {
    return { id: fromPage };
  }
  const ctx = { ...Context.allContexts, ...scopedContexts }[name];
  const ctxPkVars = contextPkVars(ctx);
  if (!ctx?.entityType || !ctxPkVars) {
    return { reason: `no "${name}" context is in scope here` };
  }
  const pkFields = Object.keys(ctxPkVars);
  const app = App.get(ctx.appName || "CreatorStudio");
  if (!pkFields.length || !app) {
    return { reason: `the "${name}" context declares no primary key` };
  }
  if (!sourceField && !isEmptyRef(ctxPkVars[pkFields[0]])) {
    return { id: ctxPkVars[pkFields[0]] };
  }
  const entityPk = sourceField && !isEmptyRef(fromPage)
    ? { [pkFields[0]]: fromPage }
    : ctxPkVars;
  const entityType = stripAppPrefix(ctx.entityType);
  const realId = app.getEntityField(entityType, entityPk, sourceField || pkFields[0]);
  if (!isEmptyRef(realId)) {
    return { id: realId };
  }
  return {
    reason: sourceField
      ? `${entityType} ${Object.values(entityPk).join(",")} has no "${sourceField}" — that relation is empty, `
        + "so the link has nothing to point at. Bind the context directly instead of through a field."
      : `the "${name}" context resolved to no entity`,
  };
}

export function resolveContextEntityHref(contextSource, pageTemplate, webBase, scopedContexts, pageEntities, { quiet = false } = {}) {
  if (!contextSource?.sourceContext || !pageTemplate || !webBase) {
    return null;
  }
  const { id: realId, reason } = resolveContextEntityId(
    contextSource.sourceContext, unwrap(scopedContexts), unwrap(pageEntities), contextSource.sourceField,
  );
  if (isEmptyRef(realId)) {
    if (!quiet) {
      // Otherwise this link silently ships its raw `{var}` template as the href.
      // eslint-disable-next-line no-console
      console.error(
        `[click-action] ${reason} — the raw page template will be used instead`,
        {
          sourceContext: contextSource.sourceContext,
          sourceField: contextSource.sourceField ?? null,
          entityType: contextSource.entityType,
          pageTemplate,
        },
      );
    }
    return null;
  }
  const filled = String(pageTemplate).replace(/\{[^}]+\}/g, realId);
  const path = filled.startsWith("/") ? filled.slice(1) : filled;
  return `${webBase}/${path}`;
}

// The pre-resolved pageEntities shortcut only holds when the wanted field IS the
// pk; a non-pk read needs the instance, so fall through to getEntityField.
function resolveUriVarValue(sourceContext, field, scopedContexts, pageEntities) {
  // Scoped bag first so a resolvable var never touches the (Pinia-backed) global.
  const ctx = scopedContexts[sourceContext] ?? Context.allContexts?.[sourceContext];
  const ctxPkVars = contextPkVars(ctx);
  if (!ctx?.entityType || !ctxPkVars) {
    return { reason: `no "${sourceContext}" context is in scope here` };
  }
  const pkFields = Object.keys(ctxPkVars);
  if (!pkFields.length) {
    return { reason: `the "${sourceContext}" context declares no primary key` };
  }
  const readField = field || pkFields[0];
  const fromPage = pageEntities[sourceContext];
  if (readField === pkFields[0] && !isEmptyRef(fromPage)) {
    return { value: fromPage };
  }
  if (readField === pkFields[0] && !isEmptyRef(ctxPkVars[pkFields[0]])) {
    return { value: ctxPkVars[pkFields[0]] };
  }
  const app = App.get(ctx.appName || "CreatorStudio");
  const value = app?.getEntityField(stripAppPrefix(ctx.entityType), ctxPkVars, readField);
  if (!isEmptyRef(value)) {
    return { value };
  }
  return { reason: `the "${sourceContext}" context has no "${readField}"` };
}

// Each {var} filled from its own uriVarSources[var]; the legacy single-entry
// contextSource fills any var lacking its own binding (back-compat).
export function resolveUriVarsHref(uriVarSources, contextSource, pageTemplate, webBase, scopedContexts, pageEntities, { quiet = false } = {}) {
  if (!pageTemplate || !webBase) {
    return null;
  }
  const scoped = unwrap(scopedContexts);
  const pages = unwrap(pageEntities);
  const names = [...String(pageTemplate).matchAll(/\{([^}]+)\}/g)].map(match => match[1]);
  if (!names.length) {
    return null;
  }
  let filled = String(pageTemplate);
  for (const name of names) {
    const source = uriVarSources?.[name];
    let value;
    let reason;
    if (source?.sourceContext) {
      ({ value, reason } = resolveUriVarValue(source.sourceContext, source.field, scoped, pages));
    } else if (contextSource?.sourceContext) {
      ({ id: value, reason } = resolveContextEntityId(contextSource.sourceContext, scoped, pages, contextSource.sourceField));
    } else {
      reason = `{${name}} has no source configured`;
    }
    if (isEmptyRef(value)) {
      if (!quiet) {
        // eslint-disable-next-line no-console
        console.error(`[click-action] {${name}}: ${reason} — the raw page template will be used instead`, { pageTemplate });
      }
      return null;
    }
    filled = filled.replaceAll(`{${name}}`, String(value));
  }
  const path = filled.startsWith("/") ? filled.slice(1) : filled;
  return `${webBase}/${path}`;
}

// A page slug whose {placeholder} never got filled points nowhere, so callers
// must drop the click entirely rather than navigate to a broken URL.
export function hasUnfilledPlaceholder(value) {
  return typeof value === "string" && /\{[^}]+\}/.test(value);
}
